'use client'

import {
  Form,
  FormGroupLabel,
  FormRadio,
  FormRadioGroup,
  FormStore,
  FormSubmit,
  useFormContext,
  useFormStore,
  useStoreState,
} from '@ariakit/react'
import capitalize from 'lodash-es/capitalize'
import { ComponentProps, createContext, useCallback, useContext, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'

import {
  Button,
  ColorSwatch,
  ColorSwatchWrapper,
  Info,
  Select,
  SelectOption,
  SizeSelectorSelectorButton,
  Tooltip,
  TypeStyle,
  TypeStyleHeadlineMicro,
  focusOutline,
  untilMd,
  withoutHoverSupport,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import {
  ProductCatalogVariant,
  VariantSelectionFormColorOptionFragment,
  VariantSelectionFormFitOptionFragment,
  VariantSelectionFormGroupFieldsFragment,
  VariantSelectionFormSizeOptionFragment,
  VariantSelectionFormVariantFieldsFragment,
} from '../../../__generated__/graphql/catalog/graphql'
import { convertApiDataToNewCartItem } from '../../../brunswick/containers/cart/utils'
import { CartErrorMessage } from '../../../components/atoms/CartErrorMessage/CartErrorMessage'
import { SizeChart } from '../../../components/pages/products/ProductPage/DetailsSection/SizesSection/SizeChart'
import {
  WaitlistModalProvider,
  useWaitlistModal,
} from '../../../components/pages/products/ProductPage/WaitlistModal/WaitlistModal'
import { trackEvent } from '../../../lib/analytics'
import { useNextQuery } from '../../_hooks/useNextQuery'
import { usePrettySizeName } from '../../_hooks/usePrettySizeName'
import {
  useCart,
  useCartDialog,
  useCartInterstitialDialog,
} from '../../_providers/CartProvider.client'
import { useFeatureFlag } from '../../_providers/ExperimentationProvider.client'
import { useLocalization } from '../../_providers/LocalizationProvider.client'
import { usePreviewDirectives } from '../../_providers/PreviewDirectivesProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { groupColorOptionsByColorGroup } from '../../_utils/groupColorOptionsByColorGroup'
import { useNextDialogContext } from '../dialogs/NextDialog.client'
import { FormError } from '../form/FormError'
// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/deprecate-formik
import { FormSubmitContent } from '../formik/FormikFormSubmit'

export const variantSelectionFormColorOption = gql(`
  fragment VariantSelectionFormColorOption on ProductCatalogProductColorOption {
    color {
      id
      hexCodes
      displayValue
      swatchImage {
        url
      }
      group {
        id
        displayValue
      }
    }
    outOfStock
  }
`)

export const variantSelectionFormFitOption = gql(`
  fragment VariantSelectionFormFitOption on ProductCatalogProductFitOption {
    fit {
      id
      displayValue
    }
    outOfStock
  }
`)

export const variantSelectionFormSizeOption = gql(`
  fragment VariantSelectionFormSizeOption on ProductCatalogProductSizeOption {
    size {
      id
      displayValue
    }
    outOfStock
  }
`)

export const variantSelectionFormVariantFields = gql(`
  fragment VariantSelectionFormVariantFields on ProductCatalogVariant {
    __typename
    id
    sku
    availability
    shopifyId
    shopifyProductId
    urlPath
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
    }
    size {
      id
      displayValue
    }
    fit {
      id
      displayValue
    }
  }
`)

export const variantSelectionFormGroupFields = gql(`
  fragment VariantSelectionFormGroupFields on ProductCatalogVariantGroup {
    __typename
    id
    title
    availability
    siteWidePromoEligible
    finalSale
    primaryCategory {
      id
    }
    color {
      id
      hexCodes
      displayValue
      group {
        id
        displayValue
      }
    }
    variant(fit: $fitFieldValue, size: $sizeFieldValue) {
      ...VariantSelectionFormVariantFields
    }
  }
`)

export const variantSelectionFormProductFields = gql(`
  fragment VariantSelectionFormProductFields on ProductCatalogStandardProduct {
    __typename
    id
    handle
    options(color: $colorFieldValue, fit: $fitFieldValue, size: $sizeFieldValue, collectionHandle: $collectionHandle) {
      colors {
        ...VariantSelectionFormColorOption
      }
      fits {
        ...VariantSelectionFormFitOption
      }
      sizes {
        ...VariantSelectionFormSizeOption
      }
    }
  }
`)

export type VariantSelectionFormContextValue = {
  product: {
    handle: string
    options: {
      colors?: VariantSelectionFormColorOptionFragment[]
      fits?: VariantSelectionFormFitOptionFragment[]
      sizes?: VariantSelectionFormSizeOptionFragment[]
    }
  }
  variantGroup: {
    title: string
    color: VariantSelectionFormGroupFieldsFragment['color']
    availability: VariantSelectionFormGroupFieldsFragment['availability']
  } & Parameters<typeof convertApiDataToNewCartItem>[0]['variantGroup']
  selectedVariant?: VariantSelectionFormSelectedVariant
  loading: boolean
  trackingCategory: string
}

const VariantSelectionFormContext = createContext<VariantSelectionFormContextValue | undefined>(
  undefined
)

type VariantSelectionFormState = {
  color: string
  fit: string | undefined | null
  size: string | undefined | null
}

type UseVariantSelectionFormProps = {
  defaultValues: {
    initialColor: string
    initialFit: string | undefined | null
    initialSize: string | undefined | null
  }
}

export const useVariantSelectionForm = ({ defaultValues }: UseVariantSelectionFormProps) => {
  const translations = useTranslation()

  const formStore = useFormStore<VariantSelectionFormState>({
    defaultValues: {
      color: defaultValues.initialColor,
      fit: defaultValues.initialFit,
      size: defaultValues.initialSize,
    },
  })

  formStore.useValidate(() => {
    const sizeFieldName = 'size' as const
    const value = formStore.getValue(sizeFieldName)
    if (!value || value.length === 0) {
      formStore.setError(sizeFieldName, translations.sizesSection.sizeNotSelectedErrorMessage)
    }
  })

  const colorFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['color']>('color') ??
    defaultValues.initialColor

  const fitFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['fit']>('fit') ?? ''

  const sizeFieldValue =
    formStore.useValue<ReturnType<(typeof formStore)['getState']>['values']['size']>('size') ?? ''

  return {
    formStore,
    queryVariables: {
      colorFieldValue,
      sizeFieldValue,
      fitFieldValue,
    },
  }
}

export const VariantSelectionFormFields = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${o => o.theme.spacing(6)};
`

export const VariantSelectionColorRadioGroup = () => {
  const { product, variantGroup, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { includeUnpublishedCmsContent } = usePreviewDirectives()
  const formContext = useFormContext()
  const translations = useTranslation()
  const value = formContext!.useValue<VariantSelectionFormState['color']>('color')
  const option = product.options.colors?.find(colorOption => colorOption.color.id === value)
  const colorGroups = groupColorOptionsByColorGroup(product.options.colors ?? [])
  const renderSoldOutLabel = variantGroup.availability === 'OUT_OF_STOCK'

  const { data: tooltipData } = useNextQuery(
    gql(`
      query GetColorGroupTooltips($handle: String!, $preview: Boolean = false) {
        siteFixtures(handle: $handle, preview: $preview) {
          __typename
          id
          tooltips {
            id
            contentType
            enumValue {
              name
            }
            title
            description
          }
        }
      }
    `),
    {
      variables: {
        handle: 'default',
        preview: includeUnpublishedCmsContent,
      },
    }
  )

  return (
    <div>
      <ColorGroupRadioGroup>
        {colorGroups.map(colorGroup => {
          const tooltip =
            colorGroups.length > 1
              ? tooltipData?.siteFixtures.tooltips?.find(
                  t => t.contentType === 'colorCategory' && t.enumValue.name === colorGroup.group.id
                )
              : undefined
          return (
            <div key={colorGroup.group.id}>
              <StyledFormGroupLabel>
                {colorGroups.length > 1
                  ? colorGroup.group.displayValue
                  : translations.detailsSection.colorTitle}
                {tooltip ? (
                  <VariantSelectionFormTooltip
                    title={tooltip.title}
                    description={tooltip.description}
                  />
                ) : null}
                {option?.color.group?.id === colorGroup.group.id ? (
                  <>
                    <FormValue>
                      {option.color.displayValue}
                      {renderSoldOutLabel ? (
                        <>
                          &nbsp;
                          <VariantSelectionFromSoldOutMessage />
                        </>
                      ) : null}
                    </FormValue>
                  </>
                ) : null}
              </StyledFormGroupLabel>

              <OptionsRow>
                {colorGroup.colorOptions.map(colorOption => {
                  const disabled = colorOption.outOfStock
                  const isSelected = value === colorOption.color.id

                  return (
                    <FormRadio
                      key={colorOption.color.id}
                      name='color'
                      value={colorOption.color.id}
                      required
                      render={htmlProps => (
                        <label
                          htmlFor={htmlProps.id}
                          {...trackEvent({
                            action: 'click',
                            category: trackingCategory,
                            label: 'Color Swatch',
                            value: colorOption.color.id,
                          })}
                        >
                          <InvisibleInput
                            {...htmlProps}
                            aria-label={colorOption.color.displayValue}
                          />
                          <ColorSwatch
                            hexCodes={colorOption.color.hexCodes ?? []}
                            swatchUrl={colorOption.color.swatchImage?.url}
                            selected={isSelected}
                            unavailable={disabled}
                            aria-label={colorOption.color.displayValue}
                            aria-hidden='true'
                            size='medium'
                          />
                        </label>
                      )}
                    />
                  )
                })}
              </OptionsRow>
            </div>
          )
        })}
      </ColorGroupRadioGroup>
      <FormError name='color' />
    </div>
  )
}

const useVariantSelectionFitField = () => {
  const { selectedVariant, product, variantGroup } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const formContext = useFormContext()
  const value = formContext!.useValue<VariantSelectionFormState['fit']>('fit') ?? ''
  const option = product.options.fits?.find(fitOption => fitOption.fit.id === value)
  const displayValue = option?.fit.displayValue ?? value
  const hide = displayValue.length > 0 && product.options.fits?.length === 1
  const renderSoldOutLabel = option?.outOfStock && variantGroup.availability === 'AVAILABLE'

  if (value.length === 0 && selectedVariant?.fit?.id) {
    formContext?.setValue('fit', selectedVariant.fit.id)
  }

  const soldOutLabelPostfix = useMemo(() => {
    return renderSoldOutLabel ? (
      <>
        &nbsp;
        <VariantSelectionFromSoldOutMessage />
      </>
    ) : null
  }, [renderSoldOutLabel])

  const setValue = useCallback(
    (newSize: string) => {
      formContext!.setValue('fit', newSize)
    },
    [formContext]
  )

  const error = useStoreState(formContext, () => formContext?.getError('fit'))

  const renderAsDisplayValue = useCallback(
    (value: string) => {
      const fitOption = product.options.fits?.find(fitOption => fitOption.fit.id === value)
      const fitDisplayText = fitOption?.outOfStock
        ? `${capitalize(fitOption.fit.displayValue)} ${
            translations.giftCard.dropdownSoldOutOptionText
          }`
        : fitOption
        ? capitalize(fitOption.fit.displayValue)
        : capitalize(value)
      return <TypeStyleHeadlineMicro>{fitDisplayText}</TypeStyleHeadlineMicro>
    },
    [product.options.fits, translations.giftCard.dropdownSoldOutOptionText]
  )

  const labelText =
    variantGroup.primaryCategory.id === 'Scrub Top'
      ? translations.pages.product.scrubTopsFitTitle
      : variantGroup.primaryCategory.id === 'Scrub Pants'
      ? translations.pages.product.scrubPantsFitTitle
      : translations.magnolia.reviewsPage.filters.filter.Fit.title

  return {
    labelText,
    labelDisplayValue: capitalize(displayValue),
    soldOutLabelPostfix,
    value,
    renderAsDisplayValue,
    setValue,
    error,
    hide,
  }
}

export const VariantSelectionFitRadioGroup = () => {
  const { product, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { value, hide, labelText, labelDisplayValue, soldOutLabelPostfix } =
    useVariantSelectionFitField()

  return (
    <div style={{ display: hide ? 'none' : 'block' }}>
      <FormRadioGroup>
        <StyledFormGroupLabel>
          {labelText}{' '}
          {labelDisplayValue.length > 0 ? (
            <FormValue>
              {labelDisplayValue}
              {soldOutLabelPostfix}
            </FormValue>
          ) : null}
        </StyledFormGroupLabel>
        <OptionsRow>
          {product.options.fits?.map(fitOption => {
            const fit = fitOption.fit
            const disabled = fitOption.outOfStock
            return (
              <FormRadio
                name={'fit'}
                key={fit.id}
                value={fit.id}
                required
                render={props => {
                  return (
                    <label
                      htmlFor={props.id}
                      {...trackEvent({
                        action: 'click',
                        category: trackingCategory,
                        label: 'Fit',
                        value: fit.id,
                      })}
                    >
                      <InvisibleInput {...props} aria-label={fit.displayValue} />
                      <Selector
                        aria-hidden={true}
                        aria-label={fit.displayValue}
                        isSelected={value === fit.id}
                        soldOut={disabled}
                        as='div'
                      >
                        {capitalize(fit.displayValue)}
                      </Selector>
                    </label>
                  )
                }}
              />
            )
          })}
        </OptionsRow>
        <FormError name='fit' />
      </FormRadioGroup>
    </div>
  )
}

type VariantSelectionFitSelectProps = {
  onChange?: (newValue: string) => void
}

export const VariantSelectionFitSelect = ({
  onChange: onChangeProp,
}: VariantSelectionFitSelectProps) => {
  const { product, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { hide, labelText, setValue, renderAsDisplayValue, value, error } =
    useVariantSelectionFitField()
  const onChange = useCallback(
    (newValue: string) => {
      onChangeProp?.(newValue)
      setValue(newValue)
    },
    [onChangeProp, setValue]
  )

  return (
    <div style={{ display: hide ? 'none' : 'block' }}>
      <Select
        value={value}
        required
        onChange={onChange}
        name={'fit'}
        renderSelectValue={renderAsDisplayValue}
        isInvalid={!!error}
        heading={labelText}
      >
        {product.options.fits?.map(fitOption => {
          const fit = fitOption.fit
          return (
            <SelectOption
              key={fit.id}
              value={fit.id}
              {...trackEvent({
                action: 'click',
                category: trackingCategory,
                label: 'Fit',
                value: fit.id,
              })}
            >
              {renderAsDisplayValue(fit.id)}
            </SelectOption>
          )
        })}
      </Select>
      <FormError name='fit' />
    </div>
  )
}

const useVariantSelectionSizeField = ({ autoSelectFirst }: { autoSelectFirst: boolean }) => {
  const { selectedVariant, product, variantGroup } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const formContext = useFormContext()
  const value = formContext!.useValue<VariantSelectionFormState['size']>('size') ?? ''
  const option = product.options.sizes?.find(sizeOption => sizeOption.size.id === value)
  const displayValue = option?.size.displayValue ?? value
  const renderSoldOutLabel = option?.outOfStock && variantGroup.availability === 'AVAILABLE'

  if (
    (product.options.sizes?.length === 1 || autoSelectFirst) &&
    value.length === 0 &&
    selectedVariant?.size?.id
  ) {
    formContext?.setValue('size', selectedVariant.size.id)
  }

  const prettyDisplayValue = usePrettySizeName({
    id: value,
    displayValue: displayValue,
  })

  const soldOutLabelPostfix = useMemo(() => {
    return renderSoldOutLabel ? (
      <>
        &nbsp;
        <VariantSelectionFromSoldOutMessage />
      </>
    ) : null
  }, [renderSoldOutLabel])

  const setValue = useCallback(
    (newSize: string) => {
      formContext!.setValue('size', newSize)
    },
    [formContext]
  )

  const error = useStoreState(formContext, () => formContext?.getError('size'))

  const renderAsDisplayValue = useCallback(
    (value: string) => {
      const sizeOption = product.options.sizes?.find(sizeOption => sizeOption.size.id === value)
      const sizeDisplayText = sizeOption?.outOfStock
        ? `${sizeOption.size.displayValue} ${translations.giftCard.dropdownSoldOutOptionText}`
        : sizeOption
        ? sizeOption.size.displayValue
        : value
      return <TypeStyleHeadlineMicro>{sizeDisplayText}</TypeStyleHeadlineMicro>
    },
    [product.options.sizes, translations.giftCard.dropdownSoldOutOptionText]
  )

  return {
    labelText: translations.quickBuy.modal.size,
    labelDisplayValue: prettyDisplayValue,
    soldOutLabelPostfix,
    value,
    renderAsDisplayValue,
    setValue,
    error,
  }
}

type VariantSelectionSizeRadioGroupProps = {
  sizeChart: ComponentProps<typeof SizeChart>['sizeChart']
}

export const VariantSelectionSizeRadioGroup = ({
  sizeChart,
}: VariantSelectionSizeRadioGroupProps) => {
  const { product, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { labelText, labelDisplayValue, soldOutLabelPostfix, value } = useVariantSelectionSizeField(
    { autoSelectFirst: false }
  )

  return (
    <div>
      <FormRadioGroup>
        <StyledFormGroupLabelWrapper>
          <StyledFormGroupLabel>
            {labelText}{' '}
            {labelDisplayValue.length > 0 ? (
              <FormValue>
                {labelDisplayValue}
                {soldOutLabelPostfix}
              </FormValue>
            ) : null}
          </StyledFormGroupLabel>
          {sizeChart ? <StyledSizeChart sizeChart={sizeChart} /> : null}
        </StyledFormGroupLabelWrapper>
        <OptionsRow>
          {product.options.sizes?.map(sizeOption => {
            const size = sizeOption.size
            const disabled = sizeOption.outOfStock
            return (
              <FormRadio
                name={'size'}
                key={size.id}
                value={size.id}
                required
                render={props => {
                  return (
                    <label
                      htmlFor={props.id}
                      {...trackEvent({
                        action: 'click',
                        category: trackingCategory,
                        label: 'Size',
                        value: size.id,
                      })}
                    >
                      <InvisibleInput {...props} aria-label={size.displayValue} />
                      <Selector
                        aria-hidden={true}
                        aria-label={size.displayValue}
                        isSelected={value === size.id}
                        soldOut={disabled}
                        as='div'
                      >
                        {size.displayValue}
                      </Selector>
                    </label>
                  )
                }}
              />
            )
          })}
        </OptionsRow>
        <FormError name='size' />
      </FormRadioGroup>
    </div>
  )
}

type VariantSelectionSizeSelectProps = {
  onChange?: (newValue: string) => void
}

export const VariantSelectionSizeSelect = ({
  onChange: onChangeProp,
}: VariantSelectionSizeSelectProps) => {
  const { product, trackingCategory } = useContext(VariantSelectionFormContext)!
  const { labelText, setValue, renderAsDisplayValue, value, error } = useVariantSelectionSizeField({
    autoSelectFirst: true,
  })
  const onChange = useCallback(
    (newValue: string) => {
      onChangeProp?.(newValue)
      setValue(newValue)
    },
    [onChangeProp, setValue]
  )

  return (
    <div>
      <Select
        value={value}
        required
        onChange={onChange}
        name={'size'}
        renderSelectValue={renderAsDisplayValue}
        isInvalid={!!error}
        heading={labelText}
      >
        {product.options.sizes?.map(sizeOption => {
          const size = sizeOption.size
          return (
            <SelectOption
              key={size.id}
              value={size.id}
              {...trackEvent({
                action: 'click',
                category: trackingCategory,
                label: 'Size',
                value: size.id,
              })}
            >
              {renderAsDisplayValue(size.id)}
            </SelectOption>
          )
        })}
      </Select>
      <FormError name='size' />
    </div>
  )
}

type VariantSelectionFormSelectedVariant =
  | VariantSelectionFormVariantFieldsFragment
  | undefined
  | null

type VariantSelectionFormBasicState = {
  color: string
  fit: string | undefined | null
  size: string | undefined | null
}

type VariantSelectionFormProps<T extends VariantSelectionFormBasicState> = {
  store: FormStore<T>
} & VariantSelectionFormContextValue &
  Omit<ComponentProps<typeof Form>, 'store'>

export const VariantSelectionForm = <T extends VariantSelectionFormBasicState>({
  loading,
  product,
  variantGroup,
  selectedVariant,
  trackingCategory,
  ...props
}: VariantSelectionFormProps<T>) => {
  return (
    <VariantSelectionFormContext.Provider
      value={{
        loading,
        product,
        variantGroup,
        selectedVariant,
        trackingCategory,
      }}
    >
      <Form style={{ display: 'contents' }} resetOnSubmit={false} {...props} />
    </VariantSelectionFormContext.Provider>
  )
}

type VariantSelectionFormPurchasingSubmitProps = {
  isPrimaryCtaForPageView: boolean
} & ComponentProps<typeof Button>

export const VariantSelectionFormPurchasingSubmit = (
  props: VariantSelectionFormPurchasingSubmitProps
) => {
  const variantSelectionContext = useContext(VariantSelectionFormContext)!
  return (
    <WaitlistModalProvider
      colorDisplayValue={variantSelectionContext.variantGroup.color?.displayValue ?? ''}
      variantShopifyId={variantSelectionContext.selectedVariant?.shopifyId}
      productTitle={variantSelectionContext.variantGroup.title}
    >
      <VariantSelectionFormPurchasingSubmitImpl {...props} />
    </WaitlistModalProvider>
  )
}

const actionableAvailabilities: Array<ProductCatalogVariant['availability']> = [
  'AVAILABLE',
  'WAIT_LISTING',
]

const VariantSelectionFormPurchasingSubmitImpl = ({
  isPrimaryCtaForPageView,
  ...buttonProps
}: VariantSelectionFormPurchasingSubmitProps) => {
  const {
    selectedVariant,
    product,
    variantGroup,
    trackingCategory,
    loading: selectionLoading,
  } = useContext(VariantSelectionFormContext)!
  const translations = useTranslation()
  const { locale } = useLocalization()
  const dialogContext = useNextDialogContext()
  const { addItems, status: cartStatus, errorMessage: cartErrorMessage } = useCart()
  const { open: openCartDialog } = useCartDialog()
  const { open: openCartInterstitialDialog } = useCartInterstitialDialog()
  const { openWaitlistModal } = useWaitlistModal()
  const submittingToCartRef = useRef(false)

  /*
  This is a little hacky, but we do this so this button is not shown as loading on
  page load or if a different add to cart is pressed on the same page as this.
  */
  if (cartStatus === 'resolved') {
    submittingToCartRef.current = false
  }
  const loading =
    selectionLoading || (submittingToCartRef.current === true && cartStatus === 'pending')

  const cartInterstitialTestDecision = useFeatureFlag<
    'no-cart-interstitial' | 'yes-cart-interstitial'
  >({
    key: 'cart-interstitial-test',
    controlVariant: 'no-cart-interstitial',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: !isPrimaryCtaForPageView,
  })

  const formContext = useFormContext()
  const color = formContext!.useValue<VariantSelectionFormState['color']>('color') ?? ''
  const fit = formContext!.useValue<VariantSelectionFormState['fit']>('fit') ?? ''
  const size = formContext!.useValue<VariantSelectionFormState['size']>('size') ?? ''

  const allFieldsFilled = color.length > 0 && size.length > 0 && fit.length > 0
  const notFound = allFieldsFilled && !selectedVariant
  const disabledAvailability =
    !notFound &&
    selectedVariant?.availability &&
    !actionableAvailabilities.includes(selectedVariant.availability)
  const disabled = loading || notFound || disabledAvailability

  formContext?.useSubmit(() => {
    if (selectedVariant) {
      if (selectedVariant.availability === 'AVAILABLE') {
        submittingToCartRef.current = true
        addItems([
          convertApiDataToNewCartItem({
            locale,
            product,
            variant: selectedVariant,
            variantGroup,
          }),
        ])
        if (!cartErrorMessage) {
          dialogContext?.hide()
          if (cartInterstitialTestDecision === 'yes-cart-interstitial') {
            openCartInterstitialDialog()
          } else {
            openCartDialog()
          }
        }
      } else if (selectedVariant.availability === 'WAIT_LISTING') {
        openWaitlistModal()
      }
    }
  })

  const addToBagContent = buttonProps.children ?? translations.detailsSection.addToBagCTA

  return (
    <FormSubmit
      render={htmlProps => (
        <>
          <Button
            {...trackEvent({
              action: 'click',
              category: trackingCategory,
              label: 'Add to Bag',
            })}
            disabled={disabled}
            block
            size='large'
            {...htmlProps}
            {...buttonProps}
          >
            <FormSubmitContent
              isSubmitting={loading}
              submittingLabel={translations.magnolia.general.loading}
            >
              {!allFieldsFilled
                ? addToBagContent
                : notFound
                ? translations.detailsSection.soldOutAddToBagCTA
                : selectedVariant?.availability === 'WAIT_LISTING'
                ? translations.detailsSection.waitlistAddToBagCTA
                : selectedVariant?.availability === 'AVAILABLE'
                ? addToBagContent
                : translations.detailsSection.soldOutAddToBagCTA}
            </FormSubmitContent>
          </Button>
          {cartErrorMessage && (
            <ErrorMessageContainer>
              <CartErrorMessage errorMessage={cartErrorMessage} />
            </ErrorMessageContainer>
          )}
        </>
      )}
    />
  )
}

const OptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${o => o.theme.spacing(2)};
  width: 100%;
`

const ColorGroupRadioGroup = styled(FormRadioGroup)`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(4)};
`

const Selector = styled(SizeSelectorSelectorButton)`
  min-width: 30px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const InvisibleInput = styled.input`
  height: 0;
  width: 0;
  opacity: 0;

  &:focus-visible + ${ColorSwatchWrapper}, &:focus-visible + ${Selector} {
    ${focusOutline};
    outline-style: solid;
  }
  margin: 0;
  position: absolute;
`

const StyledFormGroupLabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${o => o.theme.spacing(2)};
`

const formGroupLabelCss = css`
  ${TypeStyle.css.headlineMicro};
  font-weight: 700;
  margin-bottom: ${o => o.theme.spacing(2)};
`

const StyledSizeChart = styled(SizeChart)`
  ${formGroupLabelCss};
`

const StyledFormGroupLabel = styled(FormGroupLabel)`
  ${formGroupLabelCss};
  display: flex;
  justify-self: flex-start;
  gap: ${o => o.theme.spacing(1)};
  text-transform: uppercase;
  ${o => o.theme.typography.effect.uppercase};
`

const FormValue = styled.span`
  color: ${o => o.theme.color.text.tertiary.on.background};
  ${TypeStyle.css.bodyMicro};
  text-transform: none;
`

const ErrorMessageContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};

  ${untilMd} {
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`

export const StyledVariantSelectionFromSoldOutMessage = styled.span`
  color: ${o => o.theme.color.stroke.critical.text.on.subtle};
  white-space: nowrap;
`

const VariantSelectionFromSoldOutMessage = () => {
  const translations = useTranslation()
  const message = translations.detailsSection.soldOutSectionTitleMessage
  return (
    <StyledVariantSelectionFromSoldOutMessage>{message}</StyledVariantSelectionFromSoldOutMessage>
  )
}

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${o => o.theme.spacing(2)};
`

const TooltipTitle = styled(TypeStyle.HeadlineMicro)`
  text-transform: none;
  ${withoutHoverSupport} {
    font-weight: 700;
    font-size: 16px;
  }
`

const TooltipDescription = styled(TypeStyle.BodySmall)`
  text-transform: none;
  font-weight: 400;
  ${withoutHoverSupport} {
    font-size: 16px;
  }
`

export type VariantSelectionFromTooltipProps = {
  title: string
  description?: string
}

export function VariantSelectionFormTooltipContent({
  title,
  description,
}: VariantSelectionFromTooltipProps) {
  return (
    <TooltipContent>
      <TooltipTitle>{title}</TooltipTitle>
      {description && <TooltipDescription>{description}</TooltipDescription>}
    </TooltipContent>
  )
}

function VariantSelectionFormTooltip(props: VariantSelectionFromTooltipProps) {
  const translations = useTranslation()
  return (
    <Tooltip content={<VariantSelectionFormTooltipContent {...props} />} placement='bottom-start'>
      <button aria-label={translations.detailsSection.additionalColorInformation}>
        <Info />
      </button>
    </Tooltip>
  )
}
